import { TimePeriod, TIME_PERIOD, DEFAULT_FORECAST_PERIOD } from '../../../utils/time-periods';

/**
 * @typedef {import('../../../types').MonthlyOrder} MonthlyOrder
 * @typedef {import('../../../types').SkuVelocityDataItem} SkuData
 * @typedef {import('../../../types').ChartDataPoint} ChartDataPoint
 * @typedef {import('../../../types').Product} Product
 * @typedef {import('../../../types').ApiResponse} ApiResponse
 */

/**
 * Generate chart data from API response for a product
 * @param {SkuData} skuData - The SKU data from the API response
 * @param {number} currentOnHand - Current on-hand stock level
 * @param {number} suggestedReorderQuantity - Suggested reorder quantity
 * @param {string} forecastPeriod - Period to forecast into the future (30days, 3months, 6months, 1year, 2years)
 * @param {number} salesTrend - Sales trend percentage (0 = no change)
 * @param {number} leadTimeDays - Lead time in days
 * @param {number} bufferStockDays - Buffer stock in days
 * @returns {Array<ChartDataPoint>} Array of data points for the chart
 */
export const generateChartDataFromApiResponse = (
  skuData,
  currentOnHand = 0,
  suggestedReorderQuantity = 0,
  forecastPeriod = TIME_PERIOD.YEAR_1,
  salesTrend = 0,
  leadTimeDays = 7,
  bufferStockDays = 3,
) => {
  if ((skuData?.monthlyOrders?.length ?? 0) === 0) {
    return /** @type {ChartDataPoint[]} */ ([]);
  }

  const now = new Date();
  const forecastCount = TimePeriod[forecastPeriod]?.dataPoints ?? TimePeriod[DEFAULT_FORECAST_PERIOD].dataPoints;
  const avgOrdersNum = skuData.averageMonthlyVelocity || 0;
  const salesTrendMath = 1 + Number(salesTrend) / 100;
  const adjustedMonthlyVelocity = avgOrdersNum * salesTrendMath;

  const avgDailyOrders = adjustedMonthlyVelocity / 30;
  const bufferStockFromDays = Math.round(avgDailyOrders * Number(bufferStockDays));
  const leadTimeStock = Math.round(avgDailyOrders * Number(leadTimeDays));
  const orderVolumeForPeriod = adjustedMonthlyVelocity;

  // Process historical data
  const historicalData = skuData.monthlyOrders.map((monthData) => {
    const [year, month] = monthData.date.split('-');
    const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
    return /** @type {ChartDataPoint} */ ({
      date: date.toISOString().split('T')[0],
      value: monthData.quantity,
      forecastValue: null,
      stockLevel: null, // Will be calculated later
      isForecast: false,
    });
  });

  // Add current day data point
  historicalData.push(
    /** @type {ChartDataPoint} */ ({
      date: now.toISOString().split('T')[0],
      value: avgOrdersNum,
      forecastValue: adjustedMonthlyVelocity,
      stockLevel: currentOnHand + suggestedReorderQuantity,
      isForecast: false,
      isCurrentDay: true,
    }),
  );

  const forecastData = [];
  // Forecasting algorithm:
  // 1. Start with current stock level
  // 2. Add suggested reorder quantity over the selected forecast period
  // 3. Add buffer stock based on average daily orders and buffer stock days
  // 4. Add lead time stock based on average daily orders and lead time days
  let forecastStockLevel = currentOnHand + suggestedReorderQuantity + bufferStockFromDays + leadTimeStock;

  for (let i = 0; i < forecastCount; i++) {
    const date = new Date(now);
    date.setMonth(date.getMonth() + (i + 1));

    if (i === 0) {
      // First forecast period uses base forecast stock level
      forecastData.push({
        date: date.toISOString().split('T')[0],
        value: null,
        forecastValue: adjustedMonthlyVelocity,
        stockLevel: forecastStockLevel,
        isForecast: true,
      });
    } else {
      // Subsequent periods subtract order volume
      forecastStockLevel -= orderVolumeForPeriod;
      forecastData.push({
        date: date.toISOString().split('T')[0],
        value: null,
        forecastValue: adjustedMonthlyVelocity,
        stockLevel: forecastStockLevel,
        isForecast: true,
      });
    }
  }

  // Combine historical and forecast data
  const data = [...historicalData, ...forecastData];

  let currentStockLevel = currentOnHand;

  const forecastStartIndex = data.findIndex((item) => item.isForecast);
  const historicalCount = forecastStartIndex !== -1 ? forecastStartIndex : data.length;

  // Process historical data in reverse to calculate past stock levels
  for (let i = historicalCount - 1; i >= 0; i--) {
    if (!data[i].isCurrentDay) {
      // Only process non-current day items as current day stock level is already calculated
      if (i < historicalCount - 1) {
        // Add the orders that happened between this point and the next
        // (since we're going backwards in time)
        currentStockLevel += data[i + 1].value;
      }

      data[i].stockLevel = Math.max(currentStockLevel, 0);
    }
  }

  return data;
};

/**
 * Generate chart data for multiple products from API response
 * @param {Array<Product>} products - Array of product objects with processed data
 * @param {ApiResponse} apiResponse - The API response data
 * @param {string} forecastPeriod - Period to forecast into the future (30days, 3months, 6months, 1year, 2years)
 * @param {number} salesTrend - Sales trend percentage (0 = no change)
 * @param {number} leadTimeDays - Lead time in days
 * @param {number} bufferStockDays - Buffer stock in days
 * @returns {{[key: string]: Array<ChartDataPoint>}} Object with product IDs as keys and chart data arrays as values
 */
export const generateChartDataBatch = (
  products,
  apiResponse,
  forecastPeriod = TIME_PERIOD.YEAR_1,
  salesTrend = 0,
  leadTimeDays = 7,
  bufferStockDays = 3,
) => {
  return products.reduce((result, product) => {
    if (product.sku && product.hasChartData) {
      const skuData = apiResponse.data.find((item) => item.skuId === product.sku);

      if (skuData) {
        return {
          ...result,
          [product.id]: generateChartDataFromApiResponse(
            skuData,
            product.currentOnHand,
            product.suggestedReorder,
            forecastPeriod,
            salesTrend,
            leadTimeDays,
            bufferStockDays,
          ),
        };
      }
    }
    return result;
  }, {});
};
